import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/api';

const GearIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2.8 L13.86 5.05 L15.6 5.76 L18.51 5.49 L18.24 8.4 L18.95 10.14 L21.2 12 L18.95 13.86 L18.24 15.6 L18.51 18.51 L15.6 18.24 L13.86 18.95 L12 21.2 L10.14 18.95 L8.4 18.24 L5.49 18.51 L5.76 15.6 L5.05 13.86 L2.8 12 L5.05 10.14 L5.76 8.4 L5.49 5.49 L8.4 5.76 L10.14 5.05 Z" />
    <circle cx="12" cy="12" r="3.4" />
  </svg>
);

function EmailActions({
  endpoint,
  requestConfig,
  templateKey,
  templateValues = {},
  recipient = '',
  from = '',
  subject = '',
  quickTitle = 'Send Document',
  quickPrompt = 'Send document to:',
  attachmentPdf = 'Document.pdf',
  attachmentExcel = 'Document.xls',
  disabled = false,
  loading = false,
  buttonClassName = 'button small blue',
  successMessage,
  onBeforeOpen,
  buildPayload,
  onSent,
  onError,
  onLoadingChange,
}) {
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickTo, setQuickTo] = useState('');
  const [quickSendExcel, setQuickSendExcel] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateAttachmentName, setTemplateAttachmentName] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [sendError, setSendError] = useState('');
  const [openOverride, setOpenOverride] = useState(null);
  const [form, setForm] = useState({
    to: '', cc: '', bcc: '', from: '', subject: '', message: '', sendExcel: false,
  });
  const effectiveEndpoint = openOverride?.endpoint || endpoint;
  const effectiveRecipient = openOverride?.recipient ?? recipient;

  const reportError = (error, fallback) => {
    console.error(fallback, error);
    const message = error?.response?.data?.error || error?.response?.data?.message || fallback;
    setSendError(message);
    onError?.(message);
  };

  const sendEmail = async (payload) => {
    if (!effectiveEndpoint || loading) return;
    onLoadingChange?.(true);
    try {
      const extraPayload = buildPayload ? buildPayload(payload) : null;
      await api.post(effectiveEndpoint, { ...payload, ...(extraPayload || {}) }, requestConfig);
      const message = typeof successMessage === 'function'
        ? successMessage(payload.to)
        : successMessage;
      setConfirmation(message || `${quickTitle.replace(/^Send\s+/i, '')} emailed to ${payload.to}.`);
      onSent?.(payload.to);
      setCustomOpen(false);
      setQuickOpen(false);
    } catch (error) {
      reportError(error, `Failed to send ${quickTitle.toLowerCase()}.`);
    } finally {
      onLoadingChange?.(false);
    }
  };

  const openQuick = async () => {
    if (disabled || loading) return;
    setSendError('');
    let nextOverride = null;
    try {
      nextOverride = await onBeforeOpen?.();
    } catch (error) {
      reportError(error, `Failed to prepare ${quickTitle.toLowerCase()}.`);
      return;
    }
    if (nextOverride === false) return;
    const nextEndpoint = nextOverride?.endpoint || endpoint;
    if (!nextEndpoint) return;
    setOpenOverride(nextOverride || null);
    setQuickTo(nextOverride?.recipient ?? recipient ?? '');
    setQuickSendExcel(false);
    setQuickOpen(true);
  };

  const quickSend = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSendError('');
    if (!quickTo.trim()) {
      const message = 'Enter an email address before sending.';
      setSendError(message);
      onError?.(message);
      return;
    }
    await sendEmail({ to: quickTo.trim(), sendExcel: quickSendExcel });
  };

  const openCustomFromQuick = async () => {
    setQuickOpen(false);
    setCustomOpen(true);
    setSendError('');
    setTemplateLoading(true);
    setTemplateAttachmentName('');
    setForm({
      to: quickTo || effectiveRecipient || '',
      cc: '',
      bcc: '',
      from: from || '',
      subject,
      message: '',
      sendExcel: quickSendExcel,
    });
    try {
      const response = await api.post(`/email/templates/${templateKey}/render`, templateValues);
      setForm((current) => ({ ...current, message: response.data?.message || '', subject: response.data?.subject || subject }));
      setTemplateAttachmentName(response.data?.attachmentName || '');
    } catch (error) {
      reportError(error, 'Failed to load the saved email template.');
    } finally {
      setTemplateLoading(false);
    }
  };

  const submitCustom = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSendError('');
    if (!form.to.trim()) {
      const message = 'Enter an email address before sending.';
      setSendError(message);
      onError?.(message);
      return;
    }
    await sendEmail({
      to: form.to.trim(),
      cc: form.cc.trim(),
      bcc: form.bcc.trim(),
      from: form.from.trim(),
      subject: form.subject.trim(),
      message: form.message,
      sendExcel: form.sendExcel,
    });
  };

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const stopOverlayEvent = (event) => event.stopPropagation();
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  return (
    <>
      <div className="invoice-email-action-stack">
        <button
          type="button"
          className={buttonClassName}
          onClick={openQuick}
          disabled={disabled || (!endpoint && !onBeforeOpen) || loading}
        >
          {loading ? 'Sending...' : 'Email'}
        </button>
      </div>

      {quickOpen && modalRoot && createPortal(
        <div className="email-quick-overlay" onMouseDown={(event) => event.target === event.currentTarget && setQuickOpen(false)}>
          <form className="email-quick-modal" onSubmit={quickSend} onClick={stopOverlayEvent} onMouseDown={stopOverlayEvent}>
            <div className="email-quick-title">
              <h3>{quickTitle}</h3>
              <button type="button" className="invoice-email-gear email-quick-gear" onClick={openCustomFromQuick} aria-label="Customize email" title="Customize email">
                <GearIcon />
              </button>
            </div>
            <label>
              <span>{quickPrompt}</span>
              <input value={quickTo} onChange={(event) => setQuickTo(event.target.value)} autoFocus />
            </label>
            <label className="email-quick-check">
              <input type="checkbox" checked={quickSendExcel} onChange={(event) => setQuickSendExcel(event.target.checked)} />
              Attach Excel
            </label>
            {sendError && <p className="email-send-error" role="alert">{sendError}</p>}
            <div className="email-quick-actions">
              <button type="submit" className="button small blue" disabled={loading}>OK</button>
              <button type="button" className="button small gray" onClick={() => setQuickOpen(false)}>Cancel</button>
            </div>
          </form>
        </div>,
        modalRoot
      )}

      {customOpen && modalRoot && createPortal(
        <div className="email-compose-overlay" onMouseDown={(event) => event.target === event.currentTarget && setCustomOpen(false)}>
          <form className="email-compose-modal" onSubmit={submitCustom} onClick={stopOverlayEvent} onMouseDown={stopOverlayEvent}>
            <div className="email-compose-title">
              <h3>Send Email</h3>
              <button type="button" onClick={() => setCustomOpen(false)} aria-label="Close email editor">x</button>
            </div>
            <label><span>To</span><input value={form.to} onChange={(event) => updateField('to', event.target.value)} autoFocus /></label>
            <label><span>CC</span><input value={form.cc} onChange={(event) => updateField('cc', event.target.value)} /></label>
            <label><span>BCC</span><input value={form.bcc} onChange={(event) => updateField('bcc', event.target.value)} /></label>
            <label><span>From</span><input value={form.from} onChange={(event) => updateField('from', event.target.value)} placeholder="Use SMTP settings default" /></label>
            <label><span>Subject</span><input value={form.subject} onChange={(event) => updateField('subject', event.target.value)} /></label>
            <label><span>Body</span><textarea value={templateLoading ? 'Loading saved template...' : form.message} onChange={(event) => updateField('message', event.target.value)} disabled={templateLoading} /></label>
            <label className="email-compose-check">
              <input type="checkbox" checked={form.sendExcel} onChange={(event) => updateField('sendExcel', event.target.checked)} />
              Attach Excel
            </label>
            <p className="email-compose-note">
              {templateAttachmentName
                ? `${templateAttachmentName}${(form.sendExcel ? attachmentExcel : attachmentPdf).match(/\.[^.]+$/)?.[0] || (form.sendExcel ? '.xlsx' : '.pdf')}`
                : (form.sendExcel ? attachmentExcel : attachmentPdf)} will be attached.
            </p>
            {sendError && <p className="email-send-error" role="alert">{sendError}</p>}
            <div className="email-compose-actions">
              <button type="submit" className="button small blue" disabled={loading || templateLoading}>{loading ? 'Sending...' : 'Send'}</button>
              <button type="button" className="button small gray" onClick={() => setCustomOpen(false)}>Cancel</button>
            </div>
          </form>
        </div>,
        modalRoot
      )}

      {confirmation && modalRoot && createPortal(
        <div className="email-confirmation-overlay">
          <div className="email-confirmation-modal" role="alertdialog" aria-modal="true" aria-label="Email sent">
            <p>{confirmation}</p>
            <button type="button" className="button small blue" onClick={() => setConfirmation('')} autoFocus>OK</button>
          </div>
        </div>,
        modalRoot
      )}
    </>
  );
}

export default EmailActions;
